import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ConsignmentPlansService } from '@app/services/consignment-plans.service';
import { ModalService } from '@app/core/services/modal.service';
import { SrRefundRow } from '@app/services/sr-refund.service';

@Component({ selector: 'tt-sr-refund-list', templateUrl: './sr-refund-list.component.html', styleUrls: ['./sr-refund-list.component.css'], standalone: false })
export class SrRefundListComponent {
    @Input() public items: SrRefundRow[] = [];
    @Output() public itemsChange = new EventEmitter<SrRefundRow[]>();
    @Input() public viewId = '';
    @Input() public navigateRows = false;
    private travelBillMode = false;
    @Input() public set travelBillToolbar(value: boolean) {
        this.travelBillMode = value;
        if (!value) return;
        this.menu.show = true;
        this.menu.records = ['option', 'plus', 'minus', 'trash', 'sort', 'resize', 'edit', 'save'].map((name, index) => ({
            item_id: name === 'save' ? '99' : String(index), item_name: name,
            glyph: ['fas fa-ellipsis-h', 'fas fa-plus', 'fas fa-minus', 'fas fa-trash', 'fas fa-sort', 'fas fa-arrows-alt-h', 'far fa-edit', 'fas fa-save'][index],
            color: 'primary', colorActive: 'warning', canActive: ['option', 'sort', 'resize', 'edit'].includes(name),
            isActive: false, isIcon: name === 'save', enabled: ['option', 'sort'].includes(name), show: index >= 4, animate: null,
        }));
    }
    public get travelBillToolbar(): boolean { return this.travelBillMode; }
    @Output() public itemClick = new EventEmitter<{ item: SrRefundRow; event: MouseEvent }>();
    public savingWidths = false;
    public error = '';
    public readonly menu: RefundListMenu = {
        enabled: true, show: false,
        records: [
            { item_id: '0', item_name: 'sort', glyph: 'fas fa-sort', color: 'primary', canActive: true, isActive: false, colorActive: 'warning', isIcon: false, enabled: true, show: true },
            { item_id: '1', item_name: 'resize', glyph: 'fas fa-arrows-alt-h', color: 'primary', canActive: true, isActive: false, colorActive: 'warning', isIcon: false, enabled: true, show: true },
            { item_id: '99', item_name: 'save', glyph: 'fas fa-save', color: 'primary', canActive: false, isActive: false, colorActive: 'warning', isIcon: true, enabled: false, show: true, animate: null },
        ],
        activate: item => { this.selectedMenuItems(item).forEach(record => { if (record.canActive) record.isActive = true; }); },
        deactivate: item => { this.selectedMenuItems(item).forEach(record => { if (record.item_name === 'resize' && record.isActive && !(this.travelBillMode && item === 'all')) void this.saveWidths(); if (record.canActive) record.isActive = false; }); },
        function: item => {
            if (item.canActive) item.isActive = !item.isActive;
            if (item.item_name === 'resize' && !item.isActive) void this.saveWidths();
            if (item.item_name === 'option') this.menu.records.filter(record => ['plus', 'minus'].includes(record.item_name)).forEach(record => record.show = item.isActive);
        },
    };
    constructor(private readonly plans: ConsignmentPlansService, private readonly modal: ModalService) {}
    private selectedMenuItems(item?: RefundListMenuItem | string): RefundListMenuItem[] {
        if (item === 'all') return this.menu.records;
        return this.menu.records.filter(record => typeof item === 'object' ? record.item_id === item.item_id : record.item_name === (item === 'resize' ? 'resize' : 'sort'));
    }
    public rowClick = (event: { item: Item; event: MouseEvent }): void => {
        if (!this.navigateRows) return;
        this.itemClick.emit({ item: event.item as SrRefundRow, event: event.event });
    };
    public async saveWidths(): Promise<void> {
        if (!this.viewId || this.savingWidths) return;
        const header = this.items[0];
        const records: { item_id: number; item_col_width?: string | number }[] = [];
        header?.item_secondary?.forEach((column, index) => {
            if (index === 0) records.push({ item_id: 0, item_col_width: header.item_col_width });
            records.push({ item_id: index + 1, item_col_width: column.item_col_width });
        });
        this.savingWidths = true;
        const save = this.menu.records.find(record => record.item_name === 'save')!;
        save.animate = true;
        try {
            const response = await this.plans.updateColumnWidths({ view_id: this.viewId, records });
            if (response.errorcode !== '0') await this.modal.show({ type: 'warning', title: 'Varsel', message: response.errormessage, buttons: [{ label: 'OK', cssClass: 'btn-warning', close: true }] });
            else { save.animate = false; setTimeout(() => save.animate = null, 2000); }
        } catch (error) { this.error = error instanceof Error ? error.message : String(error); }
        finally { this.savingWidths = false; }
    }
}

interface RefundListMenuItem {
    item_id: string; item_name: string; glyph: string; color: string; canActive: boolean; isActive: boolean;
    colorActive: string; isIcon: boolean; enabled: boolean; show: boolean; animate?: boolean | null;
}
interface RefundListMenu {
    enabled: boolean; show: boolean; records: RefundListMenuItem[];
    activate: (item?: RefundListMenuItem | string) => void;
    deactivate: (item?: RefundListMenuItem | string) => void;
    function: (item: RefundListMenuItem) => void;
}
